import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/**
 * Tipos espelhando `FinancialAccountResponseDto`.
 *
 * O tipo anterior declarava `nome`, `banco`, `agencia`, `conta`, `tipo`,
 * `saldo` e `ativo` — nenhum existe na resposta. A tela de contas bancárias
 * mostrava cartões sem nome, sem banco e com saldo vazio, e o formulário de
 * criação enviava um corpo que o backend rejeitava por falta de `code`,
 * `name` e `type`.
 */

export type AccountType =
  | 'CHECKING_ACCOUNT'
  | 'SAVINGS_ACCOUNT'
  | 'CASH'
  | 'CREDIT_CARD'
  | 'INVESTMENT'
  | 'INTERNAL';

export interface BankAccount {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  bankCode?: string | null;
  bankName?: string | null;
  bankAgency?: string | null;
  bankAccountNumber?: string | null;
  currentBalance: number;
  initialBalance: number;
  currency: string;
  isActive: boolean;
  lastReconciledAt?: string | null;
}

export interface BankAccountInput {
  code: string;
  name: string;
  type: AccountType;
  bankCode?: string;
  bankName?: string;
  bankAgency?: string;
  bankAccountNumber?: string;
  initialBalance?: number;
  currency?: string;
}

export interface AccountBalance {
  accountId: string;
  accountName: string;
  currentBalance: number;
  initialBalance: number;
  currency: string;
  lastReconciledAt: string | null;
  lastReconciledBalance: number | null;
}

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  CHECKING_ACCOUNT: 'Conta corrente',
  SAVINGS_ACCOUNT: 'Poupança',
  CASH: 'Caixa',
  CREDIT_CARD: 'Cartão de crédito',
  INVESTMENT: 'Investimento',
  INTERNAL: 'Interna',
};

export function useBankAccounts() {
  return useQuery({
    queryKey: queryKeys.finance.accounts(),
    queryFn: () => apiClient.get<BankAccount[]>('/finance/accounts'),
  });
}

export function useBankAccountBalance(accountId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.finance.accounts(), accountId, 'balance'],
    queryFn: () => apiClient.get<AccountBalance>(`/finance/accounts/${accountId}/balance`),
    enabled: !!accountId,
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BankAccountInput) =>
      apiClient.post<BankAccount>('/finance/accounts', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.accounts() });
    },
  });
}

export function useImportOfx(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return fetch(`/api/v1/finance/accounts/${accountId}/import-ofx`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }).then((r) => r.json());
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.accounts() });
    },
  });
}
